// Google Places Atocomplete Controller
// Uses the new PlaceAutocompleteElement API with fallback to legacy Autocomplete
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["address1", "address2", "city", "state", "zipCode", "country", "autocompleteContainer"]

  connect() {
    const modal = this.element.closest('.modal');
    if (modal) {
      if (modal.classList.contains('show')) {
        this.initialize();
      } else {
        $(modal).one('shown.bs.modal', () => this.initialize());
      }
    } else {
      this.initialize();
    }
  }

  disconnect() {
    if (this._autocompleteElement) {
      this._autocompleteElement.remove();
      this._autocompleteElement = null;
    }
    if (this._legacyAutocomplete) {
      google.maps.event.clearInstanceListeners(this._legacyAutocomplete);
      this._legacyAutocomplete = null;
    }
  }

  async initialize() {
    if (this._initialized) return;
    this._initialized = true;

    // Need either a container for the web component OR an address1 input to attach to
    if (!this.hasAutocompleteContainerTarget && !this.hasAddress1Target) return;

    const apiKey = this.element.dataset.googlePlacesApiKey;
    if (!apiKey) {
      console.error('[GooglePlaces] No API key found');
      return;
    }

    try {
      this.setupBootstrapLoader(apiKey);
      const placesLib = await google.maps.importLibrary('places');

      if (this.hasAutocompleteContainerTarget) {
        // Mode 1: Separate search box (PlaceAutocompleteElement)
        if (placesLib.PlaceAutocompleteElement) {
          this.initNewAutocomplete(placesLib.PlaceAutocompleteElement);
        } else if (placesLib.Autocomplete) {
          this.initLegacyAutocomplete(placesLib.Autocomplete);
        }
      } else if (this.hasAddress1Target && placesLib.PlaceAutocompleteElement) {
        // Mode 2: Replace address1 with PlaceAutocompleteElement inline
        this.initInlineAutocomplete(placesLib.PlaceAutocompleteElement);
      }
    } catch (error) {
      console.error('[GooglePlaces] Failed to initialize:', error);
    }
  }

  setupBootstrapLoader(apiKey) {
    if (window.google?.maps?.importLibrary) return;

    ((g) => {
      var h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary", q = "__ib__", m = document, b = window;
      b = b[c] || (b[c] = {});
      var d = b.maps || (b.maps = {});
      var r = new Set, e = new URLSearchParams;
      var u = () => h || (h = new Promise(async (f, n) => {
        await (a = m.createElement("script"));
        e.set("libraries", [...r] + "");
        for (k in g) e.set(k.replace(/[A-Z]/g, t => "_" + t[0].toLowerCase()), g[k]);
        e.set("callback", c + ".maps." + q);
        a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
        d[q] = f;
        a.onerror = () => h = n(Error(p + " could not load."));
        a.nonce = m.querySelector("script[nonce]")?.nonce || "";
        m.head.append(a);
      }));
      d[l] ? console.warn(p + " only loads once. Ignoring:", g) : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n));
    })({
      key: apiKey,
      v: "weekly",
    });
  }

  // Find the internal <input> inside the closed shadow DOM via minified property.
  // See: https://stackoverflow.com/questions/78866163
  _findInternalInput(element) {
    if (!element) return null;
    for (const key of Object.getOwnPropertyNames(element)) {
      try {
        if (element[key] instanceof HTMLInputElement) return element[key];
      } catch (e) {}
    }
    return null;
  }

  // Style the entire component via JS — no CSS dependency needed.
  _styleInternals() {
    const input = this._findInternalInput(this._autocompleteElement);
    if (!input || !input.parentElement) return false;

    // Set placeholder and pre-populate with existing value if available
    input.setAttribute('placeholder', 'Search address');
    if (this._initialAddress1Value) {
      input.value = this._initialAddress1Value;
      this._initialAddress1Value = null;
    }

    // Style the outer gmp-place-autocomplete element
    const el = this._autocompleteElement;
    el.style.cssText = 'display:block;width:100%;border:none;background:transparent;padding:0;margin:0;box-shadow:none;outline:none;min-height:0;';

    // Style the input — remove its own border so container border is the only one
    input.style.cssText = 'border:none !important;background:transparent !important;box-shadow:none !important;outline:none !important;font-size:0.875rem;font-family:inherit;color:#363642;padding:0.375rem 0.5rem;height:100%;width:100%;';

    // Style .input-container (wraps icon + input) to match Bootstrap .form-control
    const container = input.parentElement;
    container.style.cssText = 'border:1px solid #c6c9d5;border-radius:0.25rem;width:100%;height:2.25rem;box-shadow:inset 0 1px 0 0 rgba(34,34,48,0.05);transition:border-color 0.15s ease-in-out,box-shadow 0.15s ease-in-out;background-color:#fff;';

    // Style .widget-container for full width
    const widget = container.parentElement;
    if (widget) widget.style.width = '100%';

    // Focus/blur: match Bootstrap focus ring
    if (!this._focusListenerAdded) {
      this._focusListenerAdded = true;
      input.addEventListener('focus', () => {
        container.style.borderColor = '#346CB0';
        container.style.boxShadow = '0 0 0 1px #346CB0';
      });
      input.addEventListener('blur', () => {
        container.style.borderColor = '#c6c9d5';
        container.style.boxShadow = 'inset 0 1px 0 0 rgba(34, 34, 48, 0.05)';
      });
    }

    return true;
  }

  // Keep the hidden address1 field in sync with what the user types in
  // the PlaceAutocompleteElement so freeform input is not lost on submit.
  _setupInputSync(hiddenInput) {
    const internalInput = this._findInternalInput(this._autocompleteElement);
    if (!internalInput) return;

    internalInput.addEventListener('input', () => {
      if (!this._placeJustSelected) {
        hiddenInput.value = internalInput.value;
      }
    });
  }

  initNewAutocomplete(PlaceAutocompleteElement) {
    this._autocompleteElement = new PlaceAutocompleteElement();
    this.autocompleteContainerTarget.appendChild(this._autocompleteElement);

    // Style internals once shadow DOM is ready
    const tryStyle = (attempts = 0) => {
      if (attempts > 20 || !this._autocompleteElement) return;
      if (!this._styleInternals()) {
        setTimeout(() => tryStyle(attempts + 1), 100);
      }
    };
    requestAnimationFrame(() => tryStyle());

    const handler = async (event) => {
      try {
        let place;
        if (event.placePrediction) {
          place = event.placePrediction.toPlace();
        } else if (event.place) {
          place = event.place;
        } else if (event.detail?.place) {
          place = event.detail.place;
        } else {
          return;
        }

        await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
        this.fillInAddressNew(place);
      } catch (error) {
        console.error('[GooglePlaces] Error fetching place details:', error);
      }
    };

    this._autocompleteElement.addEventListener('gmp-placeselect', handler);
    this._autocompleteElement.addEventListener('gmp-select', handler);
  }

  // Inline mode: replace address1 visually with PlaceAutocompleteElement
  initInlineAutocomplete(PlaceAutocompleteElement) {
    const address1 = this.address1Target;
    const parent = address1.parentElement;

    // Save existing value to pre-populate the autocomplete input
    this._initialAddress1Value = address1.value || '';

    // Hide address1 — it stays in the DOM as hidden for form submission
    address1.type = 'hidden';

    // Insert PlaceAutocompleteElement in its place
    this._autocompleteElement = new PlaceAutocompleteElement();
    parent.insertBefore(this._autocompleteElement, address1);

    // Style internals once shadow DOM is ready, then wire up input sync
    const tryStyle = (attempts = 0) => {
      if (attempts > 20 || !this._autocompleteElement) return;
      if (!this._styleInternals()) {
        setTimeout(() => tryStyle(attempts + 1), 100);
      } else {
        this._setupInputSync(address1);
      }
    };
    requestAnimationFrame(() => tryStyle());

    const handler = async (event) => {
      this._placeJustSelected = true;
      try {
        let place;
        if (event.placePrediction) {
          place = event.placePrediction.toPlace();
        } else if (event.place) {
          place = event.place;
        } else if (event.detail?.place) {
          place = event.detail.place;
        } else {
          this._placeJustSelected = false;
          return;
        }

        await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
        this.fillInAddressNew(place);
      } catch (error) {
        console.error('[GooglePlaces] Error fetching place details:', error);
      } finally {
        setTimeout(() => { this._placeJustSelected = false; }, 500);
      }
    };

    this._autocompleteElement.addEventListener('gmp-placeselect', handler);
    this._autocompleteElement.addEventListener('gmp-select', handler);
  }

  initLegacyAutocomplete(Autocomplete) {
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'form-control';
    input.placeholder = 'Search address';
    this.autocompleteContainerTarget.appendChild(input);

    this._legacyAutocomplete = new Autocomplete(input, {
      types: ['address'],
      componentRestrictions: { country: ['us', 'ca'] },
      fields: ['address_components']
    });

    this._legacyAutocomplete.addListener('place_changed', () => {
      const place = this._legacyAutocomplete.getPlace();
      if (place && place.address_components) {
        this.fillInAddressLegacy(place);
      }
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });
  }

  fillInAddressNew(place) {
    if (!place.addressComponents) return;

    const components = {};
    place.addressComponents.forEach((component) => {
      const type = component.types[0];
      components[type] = { long: component.longText, short: component.shortText };
    });

    this.populateFields(components);
  }

  fillInAddressLegacy(place) {
    if (!place.address_components) return;

    const components = {};
    place.address_components.forEach((component) => {
      const type = component.types[0];
      components[type] = { long: component.long_name, short: component.short_name };
    });

    this.populateFields(components);
  }

  populateFields(components) {
    if (this.hasAddress1Target) {
      const streetAddress = [
        components.street_number?.short || '',
        components.route?.long || ''
      ].filter(Boolean).join(' ');
      if (streetAddress) this.address1Target.value = streetAddress;
    }

    if (this.hasAddress2Target && components.subpremise) {
      this.address2Target.value = components.subpremise.short;
    }

    if (this.hasCityTarget) {
      this.cityTarget.value = (components.locality?.long || components.sublocality_level_1?.long || '');
    }

    if (this.hasStateTarget && components.administrative_area_level_1) {
      const stateCode = components.administrative_area_level_1.short;
      const target = this.stateTargets.find(t => !t.disabled) || this.stateTarget;
      if (target.tagName === 'SELECT') {
        const option = Array.from(target.options).find(
          opt => opt.value === stateCode || opt.text === stateCode
        );
        if (option) target.value = option.value;
      } else {
        target.value = stateCode;
      }
    }

    if (this.hasZipCodeTarget && components.postal_code) {
      this.zipCodeTarget.value = components.postal_code.short;
    }

    if (this.hasCountryTarget && components.country) {
      this.countryTarget.value = components.country.short;
      this.countryTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}
